import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { GUEST_USERNAME } from '../../models/guest-user';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User, usernameAsString } from '../../models/user';
import { AppService } from '../../services/app.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CustomerService } from '../../services/customer.service';
import { ErrorService } from '../../services/error.service';
import { GlobalService } from '../../services/global.service';
import { NavigationService } from '../../services/navigation.service';
import { SnackbarService } from '../../services/snackbar.service';
import { SpinnerService } from '../../services/spinner.service';
import { UserService } from '../../services/user.service';
import { ModalComponent } from '../modal/modal.component';
import { PasswordResetModalComponent } from '../password-reset-modal/password-reset-modal.component';
import { CustomizationService } from 'src/app/services/customization.service';
import { SsoService } from 'src/app/services/sso.service';
import { MfaChallengeService } from '../../services/mfa-challenge.service';
import { MfaChallengeComponent, MfaChallengeDialogData, MfaChallengeResult } from '../mfa-challenge/mfa-challenge.component';
import { MfaMethodDisabledComponent, MfaMethodDisabledResult } from '../mfa-method-disabled/mfa-method-disabled.component';
import { MfaMethodDisabledNotificationData, ENROLLMENT_FAILED_REASON } from '../../models/mfa';

@Component({
	selector: 'app-login-modal',
	templateUrl: './login-modal.html',
	styleUrls: ['./login-modal.scss']
})

export class LoginModalComponent implements OnInit, OnDestroy {
	public buildTimestamp: any;
	public companyLogo: string;
	public form: UntypedFormGroup;
	public selectedCustomer: any;
	public selectedTab = 'inet';
	public showPassword = false;
	public subscriptionsUnsubscribe = new Subject<void>();
	public user: User;
	@ViewChild('username') public username: ElementRef;
	@ViewChild('password') public password: ElementRef;
	public version: any;
	public year: number;
	public widgetIsShown: boolean = false;
	public displayPasswordBox: boolean = false;
	public hasSso: boolean = false;
	public showAccountOrLoginCreationLink: boolean = false;

	constructor(private _appService: AppService,
				private _router: Router,
				public dialogRef: MatDialogRef<LoginModalComponent>,
				@Inject(MAT_DIALOG_DATA) public data: any,
				private _dialog: MatDialog,
				private _errorService: ErrorService,
				private _formBuilder: UntypedFormBuilder,
				private _snackbarService: SnackbarService,
				private _spinnerService: SpinnerService,
				private _authenticationService: AuthenticationService,
				private _userService: UserService,
				private _globalService: GlobalService,
				private _cdRef: ChangeDetectorRef,
				private _customerService: CustomerService,
				private _customizationService: CustomizationService,
				private _ssoService: SsoService,
				private _navigationService: NavigationService,
				private _mfaChallengeService: MfaChallengeService) {
	}

	close() {
		this.dialogRef.close();
	}

	continue() {
		//if we can see the password box, we shouldn't be able to see the continue button anyway,
		//so there's nothing to do
		if(this.displayPasswordBox)
			return;

		let username: string = this.form.get('username')?.value;

		//in this case validation will appear to let the user know to enter a username,
		//but we still have to prevent the rest of this method from executing
		if(!username || username.length == 0)
			return;

    username = username.toLocaleLowerCase();

		let parts = username.split('@');
		if (parts.length == 2) { //valid email address
			let domains = this.user?.globalSettings?.ssoDomains;
			let matches = domains.filter((d) => d === parts[1]);
			if (matches.length == 1) { //because domains are unique
				let info = {
					username: username,
					domain: matches[0]
				};
				localStorage.setItem('RapidShip:ssoInfo', JSON.stringify(info));

				this._ssoService.validateSsoLogin(username).subscribe({
					next: () => {
						window.location.href = this._ssoService.ssoGetLoginUrl(username);				
					}, error: (error) => {
						this._errorService.error(error, 'login Failed', 'modal');
					}
				});
				return;
			}
		}
		//if we get this far, we're not using SSO
		this.displayPasswordBox = true;
		setTimeout(() => { //the change in displayPasswordBox doesn't propagate to the ngIfs instantly, so we have to wait a bit for this.password to be defined
			this.password.nativeElement.focus();
		}, 100);
	}

	forgotPassword() {
		const dialogRef = this._dialog.open(PasswordResetModalComponent);
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				if (result === true) {
					this._snackbarService.openSnackBar(
						'We will email you a password reset link if we have the email address on record.',
						'Close', 5000, 'success');
				}
			}
		});
	}

	guestLogin() {
		this.selectTab('inet');
		this._cdRef.detectChanges();
		this.form.get('username').setValue(GUEST_USERNAME);
		this.form.get('password').setValue(GUEST_USERNAME);
		this.login(this.form);
	}

	login(form) {
		if (form.valid) {
			if (form.value.username.toString().length > 0 && form.value.password.toString().length > 0) {
				this._spinnerService.startSpinner();
				this.showPassword = false;
				this._authenticationService.initiateLogin(form.value, this.selectedTab).subscribe({
					next: () => {
						this._spinnerService.stopSpinner();
						this.onLogin();
					}, error: (error) => {
						this._spinnerService.stopSpinner();
						this._errorService.error(error, 'login Failed', 'modal');
						this._userService.deleteUser();
					}
				});
			}
		} else {
			this._snackbarService.openSnackBar('Please fill out all required fields.', 'Close', 3000);
		}
	}

	ngOnDestroy() {
		this.subscriptionsUnsubscribe.next();
	}

	ngOnInit() {
		this._appService.companyLogo$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((companyLogo) => {
			this.companyLogo = companyLogo;
		});
		this._userService.user$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((user) => {
			this.user = user;
			this.hasSso = this.user?.globalSettings?.isCentralizedSsoConfigured && this.user?.globalSettings?.ssoDomains?.length > 0;
			this.displayPasswordBox = !this.hasSso;
		});
		this._customizationService.widgetIsShown$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((widgetIsShown) => {
			this.widgetIsShown = widgetIsShown;
		});

		this.hasSso = this.user?.globalSettings?.isCentralizedSsoConfigured && this.user?.globalSettings?.ssoDomains?.length > 0;
		this.showAccountOrLoginCreationLink = this.user?.globalSettings?.canCreateCustAccount || this.user?.globalSettings?.canCreateUserLogin;
		this.displayPasswordBox = !this.hasSso;

		const rememberMe: any = JSON.parse(localStorage.getItem('Rapidship:rememberMe'));
		this.selectedTab = rememberMe ? rememberMe?.userType : 'inet';
		this.form = this._formBuilder.group({
			username: [rememberMe ? rememberMe?.username : '', [Validators.required]],
			password: ['', [Validators.required]],
			rememberMe: !!rememberMe
		})
		this.buildTimestamp = this._appService.buildTimestamp;
		this.version = this._appService.version;
		this.year = this._appService.year;
		if (this.data?.error) {
			if (this.data?.error?.error?.detailedMsg === 'You are not authorized to access this application.') {
				this.data.error.error.detailedMsg = 'You have been logged out. Please enter your login information again to continue using Rapidship.';
			}
			// XD-62456: a 401 body can lack detailedMsg (empty, string or Blob body); dereferencing it threw before
			// the explanation modal below opened, leaving a bare login screen with no reason shown.
			if (this.data?.error?.error?.detailedMsg?.indexOf('You have reached the maximum login attempts') > -1) {
				this.data.error.error.generalMsg = 'Too many failed login attempts';
			} 
			this._errorService.error(this.data?.error, 'Login Failed', 'modal');
		}
		setTimeout(() => {
			this.username?.nativeElement?.focus();
		}, 500);
	}

	onLogin() {
		if (this.selectedTab === 'inet' && this.shouldShowMethodDisabledNotification()) {
			this.openMethodDisabledNotification();
			return;
		}

		if (this.selectedTab === 'inet' && this.shouldShowEnrollmentWizard()) {
			this.openEnrollmentWizard();
			return;
		}

		if (this.selectedTab === 'inet' && this.shouldShowMfaChallenge()) {
			this.openMfaChallengeDialog();
			return;
		}

		this.proceedWithNavigation();
	}

	private shouldShowMethodDisabledNotification(): boolean {
		return this.user?.mfaNotifyPending === true;
	}

	private openMethodDisabledNotification(): void {
		const dialogData: MfaMethodDisabledNotificationData = {
			disabledMethodNames: this.user.mfaDisabledMethodNames || [],
			effectivePolicy: this.user.mfaEffectivePolicy,
			gracePeriodActive: this.user.mfaGracePeriodActive,
			gracePeriodDaysRemaining: this.user.mfaGracePeriodDaysRemaining,
			allowedMethodsForEnrollment: this.user.mfaAllowedMethodsForEnrollment || [],
			contactAdminPhone: this.user.mfaContactAdminPhone,
			contactAdminEmail: this.user.mfaContactAdminEmail
		};

		const dialogConfig: MatDialogConfig = {
			disableClose: true,
			width: '500px',
			maxWidth: '90vw',
			data: dialogData
		};

		const dialogRef = this._dialog.open(MfaMethodDisabledComponent, dialogConfig);
		dialogRef.afterClosed()
			.pipe(takeUntil(this.subscriptionsUnsubscribe))
			.subscribe((result: MfaMethodDisabledResult) => {
				if (result?.signedOut) {
					// F-132: the always-available Sign out on the notification dialog. Ends the session cleanly
					// rather than leaving the user to close the tab.
					this._authenticationService.performLogout();
					return;
				}
				if (result?.action === 'enroll') {
					this.user.mfaNotifyPending = false;
					this.user.mfaEnrollmentPending = true;
					this._userService.updateUser(this.user);
					this._userService.openMfaEnrollmentWizard()
						.pipe(takeUntil(this.subscriptionsUnsubscribe))
						.subscribe((result) => {
							if (result?.mfaDisabled) {
								this._authenticationService.authenticationDelete(result.message);
								return;
							}
							// F-135: Sign out from the enrol wizard must end the session, exactly as the sibling
							// openEnrollmentWizard() does. Without this, signedOut fell through to
							// proceedWithNavigation(): the server token was never revoked, mfaEnrollmentPending
							// stayed true (UserService skips its bookkeeping on signedOut), and the non-allowlisted
							// data cascade was short-circuited to of(null), stranding a permanent full-screen spinner.
							if (result?.signedOut) {
								this._authenticationService.performLogout();
								return;
							}
							// F-191: only enrolled/skipped may proceed. On error / force-close the wizard resolved
							// neither, and (per F-139) mfaEnrollmentPending is left true — proceeding lets the
							// interceptor short-circuit the data cascade and strand a permanent disableClose spinner
							// (the F-135 mechanism via a different door). Fail CLOSED with teardown -> login modal.
							// F-199: skip if an external teardown (timer/401) already ended the session, so we don't
							// blank its modal. F-198: pass a reason so the login screen explains the failure.
							if (!(result?.enrolled || result?.skipped)) {
								if (this._userService.getUser()?.token) {
									this._authenticationService.authenticationDelete(ENROLLMENT_FAILED_REASON);
								}
								return;
							}
							this.user = this._userService.getUser();
							if (this.shouldShowMfaChallenge()) {
								this.openMfaChallengeDialog();
								return;
							}
							this.proceedWithNavigation();
						});
				} else if (result?.action === 'continue') {
					this.user.mfaNotifyPending = false;
					this._userService.updateUser(this.user);
					// H2 (amonea): a challenge can still be owed on the Continue path (e.g. Optional policy, a
					// surviving factor after one was admin-disabled). Mirror the enroll branch and present it
					// first — otherwise proceedWithNavigation() runs the data cascade against a still-PreAuth
					// token, which the interceptor short-circuits and the dialog hangs.
					this.user = this._userService.getUser();
					if (this.shouldShowMfaChallenge()) {
						this.openMfaChallengeDialog();
						return;
					}
					this.proceedWithNavigation();
				} else {
					this._authenticationService.authenticationDelete();
				}
			});
	}

	private shouldShowEnrollmentWizard(): boolean {
		// Trust the backend's authoritative decision (mfaAction="enroll"). The old client-side re-derivation
		// (customerMfaPolicy > 0 && enrolledCount === 0 && !mfaWizardSkipped) suppressed the wizard under
		// Enforced for a stale Optional-era skip flag, leaving a PreAuth token that 401'd the post-login data
		// cascade and locked the user out. The backend already accounts for effective policy + skip semantics.
		return this.user?.mfaEnrollmentPending === true;
	}

	private openEnrollmentWizard(): void {
		this.user.mfaEnrollmentPending = true;
		this._userService.updateUser(this.user);

		this._userService.openMfaEnrollmentWizard()
			.pipe(takeUntil(this.subscriptionsUnsubscribe))
			.subscribe((result) => {
				if (result?.mfaDisabled) {
					this._authenticationService.authenticationDelete(result.message);
					return;
				}
				// Server-side DELETE + local cleanup + auth modal. The wizard only signals; it deliberately
				// does not navigate, because it can be opened from a router guard mid-navigation.
				if (result?.signedOut) {
					this._authenticationService.performLogout();
					return;
				}
				// F-191: same fail-closed guard as the notify→enrol sibling — only a genuine enrolled/skipped
				// resolution may proceed; error / force-close tears down to the login modal instead of proceeding
				// into a stranded data cascade (mfaEnrollmentPending is left true by F-139).
				// F-199 idempotent (skip if an external teardown already ran) + F-198 reason for the login modal.
				if (!(result?.enrolled || result?.skipped)) {
					if (this._userService.getUser()?.token) {
						this._authenticationService.authenticationDelete(ENROLLMENT_FAILED_REASON);
					}
					return;
				}
				this.user = this._userService.getUser();
				if (this.shouldShowMfaChallenge()) {
					this.openMfaChallengeDialog();
					return;
				}
				this.proceedWithNavigation();
			});
	}

	private shouldShowMfaChallenge(): boolean {
		return this.user?.mfaChallengePending === true;
	}

	private openMfaChallengeDialog(): void {
		const dialogData: MfaChallengeDialogData = {
			challengeSessionId: this.user.mfaChallengeSessionId,
			preferredMethod: this.user.mfaPreferredMethod,
			availableMethods: this.user.mfaAvailableMethods || [],
			maskedEmail: this.user.mfaMaskedEmail,
			otpTtlMinutes: this.user.mfaOtpTtlMinutes,
			otpResendDelaySeconds: this.user.mfaOtpResendDelaySeconds,
			maxAttempts: this.user.mfaMaxAttempts,
			contactAdminPhone: this.user.mfaContactAdminPhone,
			contactAdminEmail: this.user.mfaContactAdminEmail
		};

		const dialogConfig: MatDialogConfig = {
			disableClose: true,
			width: '500px',
			maxWidth: '90vw',
			data: dialogData
		};

		const dialogRef = this._dialog.open(MfaChallengeComponent, dialogConfig);
		dialogRef.afterClosed()
			.pipe(takeUntil(this.subscriptionsUnsubscribe))
			.subscribe((result: MfaChallengeResult) => {
				if (result?.signedOut) {
					// H5: the always-available Sign out on the login-mode challenge. End the session cleanly
					// (server DELETE + auth modal) rather than leaving the user to reload the tab.
					this._authenticationService.performLogout();
					return;
				}
				if (result?.success) {
					if (result.mfaSessionToken) {
						this._mfaChallengeService.storeMfaToken(
							usernameAsString(this.user),
							result.mfaSessionToken
						);
					}
					this.user.mfaChallengePending = false;
					// An Email OTP challenge re-proves the address (the backend flips emailVerified back on),
					// so refresh the cached flag here — otherwise the Security tab keeps showing the pre-login
					// "Not verified" until a full re-login. Covers an address edited in Ops on an already-
					// enrolled user, who is challenged (not re-enrolled) at the next login.
					if (result.method === 'EmailOtp') {
						this.user.emailVerified = true;
					}
					this._userService.updateUser(this.user);
					this.proceedWithNavigation();
				} else {
					this._authenticationService.authenticationDelete();
				}
			});
	}

	private proceedWithNavigation(): void {
		if (this.selectedTab === 'inet') {
			// Load app data only after MFA is fully resolved (token is now Active). A PreAuth token
			// would 401 on these data endpoints and trip the global logout handler.
			this._spinnerService.startSpinner();
			this._authenticationService.loadInetUserData(this.user)
				.pipe(takeUntil(this.subscriptionsUnsubscribe))
				.subscribe({
					next: () => { this._spinnerService.stopSpinner(); this.doNavigation(); },
					error: (error) => {
						this._spinnerService.stopSpinner();
						this._errorService.error(error, 'login Failed', 'modal');
						this._userService.deleteUser();
					}
				});
		} else {
			this.doNavigation();
		}
	}

	private doNavigation(): void {
		if (this.selectedTab === 'inet') {
			this.selectedCustomer = this._customerService.getSelectedCustomer();
			if (this.selectedCustomer.billingStatus !== 'CREDIT_HOLD' && (this._router.url === '/' ||
				this._router.url.includes('home') ||
				this._router.url.includes('work') ||
				this._router.url.includes('login') ||
				this._router.url.includes('register') ||
				this._router.url.includes('settlements') ||
				this._router.url.includes('settlement-detail') ||
				this._router.url.includes('estimated-settlements'))) {
				this._router.navigate([this._navigationService.resolveDefaultLanding(this.user)]);
			} else if (this.selectedCustomer.billingStatus === 'CREDIT_HOLD' && this.user?.globalSettings?.displayInvoices && this.user?.inetUser?.viewInvoices) {
				console.warn(this.selectedCustomer.billingStatus)
				this._router.navigate(['invoices']);
				const qbiMessage = `Warning!  Your customer account is on credit hold due to outstanding invoices that need immediate attention.  All invoices that have reached your ${this.selectedCustomer?.startCreditHoldEnforcementDay} aging limit must be paid in order to proceed with order placement.  Please navigate to the Invoices page or contact our customer service department for more information.`;
				const nonQbiMessage = 'Warning!  Your customer account is on credit hold due to outstanding invoices that need immediate attention. Please navigate to the Invoices page or contact our customer service department for more information.';
				const dialogConfig: MatDialogConfig = {
					disableClose: true,
					data: {
						title: 'Warning',
						message: this.user?.globalSettings?.isQuickbooksEnabled ? qbiMessage : nonQbiMessage,
						okText: 'Ok'
					}
				};
				this._dialog.open(ModalComponent, dialogConfig);
			} else {
				this._router.navigate(['home']);
			}
		} else if (this.selectedTab === 'worker') {
			if (this._router.url === '/' ||
				this._router.url.includes('home') ||
				this._router.url.includes('address-book') ||
				this._router.url.includes('login') ||
				this._router.url.includes('register') ||
				this._router.url.includes('place-order') ||
				this._router.url.includes('order-confirmation') ||
				this._router.url.includes('track-order') ||
				this._router.url.includes('route-tracking') ||
				this._router.url.includes('reports') ||
				this._router.url.includes('order-templates') ||
				this._router.url.includes('invoices')) {
				this._router.navigate(['work']);
			}
		}

		setTimeout(() => {
			this.close();
		}, 500);
	}

	removeExtraSpaces() {
		const control = this.form.get('username');
		if (control) {
			const value = control.value || '';
			control.setValue(value.trim(), { emitEvent: false });
		}
	}

	resetForm() {
		this.displayPasswordBox = false;
		this.form.get('username').setValue('');
	}

	selectTab(tab) {
		this.selectedTab = tab;
		this.form.reset();
		this.username.nativeElement.focus();
		this.displayPasswordBox = this.selectedTab === 'inet' ? !this.hasSso : true;
	}

	toggleShowPassword() {
		this.showPassword = !this.showPassword;
	}

	usernameEnter(event: KeyboardEvent) {
		if(!this.displayPasswordBox) { //we don't want to prevent form submission if we can see the password box
			event.preventDefault();
			this.continue();
		}
	}
}
